/**
 * FichaAgenteView -- #/agente/{code}. LA FICHA DE UN AGENTE (FICHA-PERSONA-AGENTE-T1, SI de Ares #33993).
 *
 * AUDIENCIA: Josse y Ares, en el escritorio (y que no se rompa en el telefono).
 * Junta lo que el panel ya lee de un agente, cada cosa con su fuente y su enlace a la vista que la tiene. Separado lo que
 * SABE (demostrado) de lo que PUEDE (autorizado), como pide el mapa de Atenea (#33972). Lo que puede sale
 * de GET admin/agents/{code}/puede (nexo-api 631): mientras de 404 dice "sin fuente todavia" y se enciende sola al publicarse.
 *
 * PETICIONES AL MONTAR: 5: pensum de los agentes, pensum comun, insignias del agente, lo que puede y
 * sus movimientos NXS. Cada una puede fallar sola: esa seccion dice "no se pudo leer", las demas se pintan.
 * Reglas en application/agentes/fichaDeAgente.js (sin DOM).
 *
 * @module presentation/views/FichaAgenteView
 */

import { PensumAgentesApiClient } from '../../infrastructure/api/PensumAgentesApiClient.js';
import { AgentBadgeApiClient } from '../../infrastructure/api/AgentBadgeApiClient.js';
import { NxsApiClient } from '../../infrastructure/api/NxsApiClient.js';
import { AgentApiClient } from '../../infrastructure/api/AgentApiClient.js';
import { normalizarPensumAgentes } from '../../application/pensum/pensumCascada.js';
import { normalizarPensumNexo } from '../../application/pensum/pensumNexo.js';
import { fichaDeAgente, fraseDeSeccion, NO_LEIDO, SIN_FUENTE } from '../../application/agentes/fichaDeAgente.js';
import { setPageTitle } from '../components/NavBar.js';
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

// La salida, igual en todas las ramas que pintan la pantalla (lineamientos de pantalla: nadie queda encerrado).
const VOLVER = regresoHtml({ volverA: '#/agents', texto: 'Agentes' });

const C = { texto: 'var(--text-strong)', suave: 'var(--text-secondary)', gris: 'var(--text-muted)', borde: 'var(--border-default)', panel: 'var(--bg-secondary)', link: 'var(--accent-blue)', ojo: 'var(--accent-yellow)' };

/** El codigo del agente desde la ruta #/agente/{code}. */
export function codigoDeLaRuta(hash = location.hash) {
  const m = String(hash).match(/^#\/agente\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

const listaDe = (r) => {
  const d = r?.data?.data ?? r?.data;
  return Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : [];
};

function seccionHTML(s) {
  const color = s.estado === NO_LEIDO ? C.ojo : s.estado === 'sin_fuente' ? C.gris : C.texto;
  const enlace = s.fuente ? `<a href="${esc(s.fuente)}" style="font-size:14px;color:${C.link}">Ver en su vista</a>` : '';
  return `<div data-fa-seccion="${esc(s.clave)}" data-fa-estado="${esc(s.estado)}" style="border:1px solid ${C.borde};border-radius:8px;padding:10px 12px;margin-bottom:8px">
    <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap">
      <strong style="font-size:14px;color:${C.texto}">${esc(s.titulo)}</strong>${enlace}
    </div>
    <div style="font-size:14px;color:${color};margin-top:4px">${esc(fraseDeSeccion(s))}</div>
  </div>`;
}

export async function renderFichaAgente(container) {
  const codigo = codigoDeLaRuta();
  setPageTitle?.(codigo ? `Agente ${codigo}` : 'Agente');
  if (!codigo) {
    container.innerHTML = `<section class="detail-section">${VOLVER}<p style="font-size:14px;color:${C.suave}">Falta el codigo del agente en la direccion.</p></section>`;
    return;
  }
  container.innerHTML = `<section class="detail-section">${VOLVER}<p style="font-size:14px;color:${C.suave}">Cargando la ficha de ${esc(codigo)}...</p></section>`;

  const [rAgentes, rNexo, rInsignias, rNxs, rPuede] = await Promise.all([
    PensumAgentesApiClient.get().catch(() => null),
    PensumAgentesApiClient.getNexo().catch(() => null),
    AgentBadgeApiClient.list({ agentCode: codigo, pageSize: 200 }).catch(() => null),
    NxsApiClient.getTransactions(codigo, 200).catch(() => null),
    AgentApiClient.puede(codigo).catch(() => null)
  ]);

  const f = fichaDeAgente(codigo, {
    pensumAgentes: rAgentes?.ok ? normalizarPensumAgentes(rAgentes.data) : null,
    pensumNexo: rNexo?.ok ? normalizarPensumNexo(rNexo.data) : null,
    insignias: rInsignias?.ok ? listaDe(rInsignias) : null,
    transacciones: rNxs?.ok ? listaDe(rNxs) : null,
    // 404 = la ruta aun no esta publicada (sin fuente); cualquier otro fallo = no se pudo leer. Se enciende sola al publicarse.
    puede: rPuede?.ok ? (rPuede.data?.data ?? rPuede.data ?? null) : rPuede?.code === 404 ? SIN_FUENTE : null
  });

  container.innerHTML = `<section class="detail-section" data-ficha-agente="${esc(f.codigo)}" style="color:${C.texto}">
    ${VOLVER}
    <h2 style="margin:6px 0 2px;font-size:18px">${esc(f.codigo)}</h2>
    <p style="font-size:14px;color:${C.suave};margin:0 0 14px">Lo que sabe esta demostrado por una evaluacion; lo que puede es lo que tiene autorizado. Son cosas distintas.</p>
    <h3 style="font-size:16px;margin:0 0 6px">Lo que sabe (demostrado)</h3>
    ${f.sabe.map(seccionHTML).join('')}
    <h3 style="font-size:16px;margin:14px 0 6px">Lo que puede (autorizado)</h3>
    ${f.puede.map(seccionHTML).join('')}
    <h3 style="font-size:16px;margin:14px 0 6px">Tambien</h3>
    ${f.tambien.map(seccionHTML).join('')}
  </section>`;
}
